<template>
  <div :class="componentClass" style="position: relative;">
    <label :class="validationClass">{{ label|capitalize }}</label>
    <v-spacer></v-spacer>
    <v-btn v-if="assistantComponent"
           @click.prevent="$emit('bcube-assistant', { inputName: name, component: assistantComponent })"
           class="mt-0 mx-0 mb-1" icon small
           style="position: absolute; top: 8px; right: 8px;"
    >
      <v-icon color="primary" v-text="'tips_and_updates'"></v-icon>
    </v-btn>

    <component
        :is="editorComponent"
        :data-vv-name="name"
        :data-vv-scope="scope"
        :data-vv-as="label"
        :disabled="disabled"
        :hint="hint"
        :options="options"
        :ref="name"
        :type="type"
        :value="value"
        :name="name"
        :label="label"
        :counterClass="counterClass"
        :counter="counterShowValue"
        :force-spellcheck-before-save="forceSpellcheckBeforeSave"
        :force-spellcheck-before-publish="forceSpellcheckBeforePublish"
        @blur="onBlur"
        @focus="onFocus"
        @input="onInput"
        @input:as-text="updateWordsCount"
        @length="updateCharactersCount"
        data-vv-value-path="value"
        v-validate="customValidation"
    ></component>

    <div class="input-group__details" :class="validationClass">
      <div class="input-group__messages">
        <div
            :key="index"
            class="input-group__error"
            v-for="(message, index) in errorMessages"
            v-html="message">
        </div>
      </div>

      <div v-if="counter" :class="counterClass">{{ counterShowValue }}</div>
    </div>
  </div>
</template>

<script>
import { isEmpty, isObject, isArray } from 'lodash';
import FormElement from '@/modules/form/mixins/form-element';
import Editor from '@/components/texteditor/QuillEditor.vue';
import CanCounterText from '@/modules/form/mixins/can-counter-text';

/**
 * FormTextEditor Component
 *
 * Include BasicEditor Component
 *
 * @property {String}   value         Text Editor content (Plain HTML)
 * @property {String}   type          Change Text Editor type
 * @property {Object}   [options]     Add more options to the Text Editor
 * @property {String}   hint          Change the default Text Editor placeholder
 * @property {Boolean}  disabled      Disable Text Editor
 * @property {Number}   limit         Text Editor letters limit
 * @property {Boolean}  counter       Show characters counter
 *
 * @fires FormTextEditor#blur      Emit String of HTML
 * @fires FormTextEditor#input     Emit String of HTML
 */

export default {
  name: 'nx-form-text-editor',
  mixins: [FormElement, CanCounterText],
  props: {
    value: [String, Array],
    type: {
      type: String,
      default: 'basic',
    },
    options: Object,
    hint: String,
    forceSpellcheckBeforeSave: Boolean,
    forceSpellcheckBeforePublish: Boolean,
  },
  data() {
    return {
      focused: false,
      editorComponent: Editor.name,
    };
  },
  computed: {
    assistantComponent() {
      return this.$attrs.bcubeAssistantComponent || this.$attrs['bcube-assistant-component'];
    },
    componentClass() {
      return {
        'form-text-editor': true,
        'input-group__editor': true,
        'input-group': true,
        'input-group--text-field': true,
        'input-group--multi-line': true,
        'input-group--focused': this.focused,
        'primary--text': true,
        'nx-primary--lighten-5': true,
        'input-group--disabled': this.disabled,
      };
    },
    validationClass() {
      return {
        'input-group--required': this.required,
        'error--text': this.errorMessages.length > 0,
        'input-group--error': this.errorMessages.length > 0,
      };
    },
    customValidation() {
      return {
        // Valid flag will be updated on updateForm callback (See below)
        ...this.formatValidationObject(this.validation),
        ...this.counterValidationObject,
      };
    },
    empty() {
      return this.textLength === 0;
    },
  },
  components: {
    [Editor.name]: Editor,
  },
  methods: {
    onBlur(html) {
      this.$parentForm.updateField(this.name, html);
      this.$emit('blur', html);
    },
    onFocus(val) {
      this.focused = val;
    },
    onInput(html) {
      this.$emit('input', html);
    },
    formatValidationObject(validation) {
      if (isEmpty(validation)) {
        return {};
      }
      if (isObject(validation)) {
        return validation;
      }
      if (isArray(validation)) {
        return validation
          .filter(rule => !isEmpty(rule))
          .reduce((result, rule) => {
            result[rule] = true;
            return result;
          }, {});
      }
      return { [validation]: true };
    },
  },
  watch: {
    softLimitReached: {
      handler(reached) {
        this.flags.recommended = reached;
      },
      immediate: true,
    },
    recommendedMinReached: {
      handler(reached) {
        this.flags.recommended = !reached; // Recommend until reached
      },
      immediate: true,
    },
  },
};
</script>

<style lang="stylus">
// base
$inputQuill_fgColor = #A0A0AD;
$inputQuill_focused = #4ED19C;

.form-text-editor {
  div.ql-toolbar.ql-snow {
    width: inherit !important;
  }
}

// style
.input-group__editor {
  margin-bottom: 24px;
  margin-top: 22px;
  padding-top: 18px !important;

  label {
    margin-left: 15px;
  }

  &.input-group--text-field:not(.input-group--single-line).input-group--focused:not(.input-group--textarea) label,
  .input-group--text-field:not(.input-group--single-line).input-group--placeholder:not(.input-group--textarea) label {
    transform: translate(0, -10px) scale(0.75);
  }

  .input-group__details {
    flex: 1 0 calc(100% - 30px);
    margin-left: 15px;
    margin-right: 15px;
    padding-bottom: 16px;

    .input-group--text-field:not(.input-group--error) .input-group__counter {
      color: $inputQuill_fgColor;
    }

    .input-group__counter--soft-limit-reached {
      color: $warning;
    }
    .input-group__counter--recommended-min-reached {
      color: $success
    }
  }
  &.input-group--disabled {
    pointer-events: none;
  }
}
</style>
